import { useCallback, useState } from 'react';

import { ICardMediaAsset } from '@/types/media';
import StudioRoutes from '@/utils/routes';
import { ActionList, Box, Button, ButtonProps, InlineStack, Popover, Text } from '@shopify/polaris';
import {
  CollectionFilledIcon,
  DomainLandingPageIcon,
  EmailIcon,
  ImportIcon,
  OutgoingIcon,
} from '@shopify/polaris-icons';

import { DownloadMediaModal } from '@/features/publish/DownloadMediaModal';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { track } from '@/utils/tracker';
import { EVENTS } from '@/constants/tracking';
import MediaAssetCardQuickViewActions from './MediaAssetCardQuickViewActions';

type MediaAssetCardActionsProps = {
  media?: ICardMediaAsset;
  disabled: boolean;
  buttonSize?: ButtonProps['size'];
};

const MediaAssetCardActions = ({ media, disabled, buttonSize = 'large' }: MediaAssetCardActionsProps) => {
  const { pushPath } = useRouterNavigation();

  const [sharePopoverActive, setSharePopoverActive] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const toggleSharePopoverActive = useCallback(
    () => setSharePopoverActive((sharePopoverActive) => !sharePopoverActive),
    []
  );

  const handlePublishToWebsite = () => {
    track(EVENTS.PUBLISH_TO_WEBSITE_CLICK);
    pushPath(StudioRoutes.publish());
  };

  const handleFeatureOnShop = () => {
    track(EVENTS.FEATURE_ON_SHOP_CLICK);
    pushPath(StudioRoutes.publish());
  };

  const handlePublishToEmailFlows = () => {
    track(EVENTS.PUBLISH_TO_EMAIL_FLOW_CLICK);
    pushPath(StudioRoutes.publish());
  };

  return (
    <>
      <InlineStack align="space-between">
        <InlineStack gap="200">
          <Popover
            active={sharePopoverActive}
            activator={
              <Button onClick={toggleSharePopoverActive} icon={OutgoingIcon} size={buttonSize} disabled={disabled} />
            }
            onClose={toggleSharePopoverActive}
          >
            <Popover.Pane>
              <Popover.Section>
                <Text as="h3" variant="headingMd">
                  Publish
                </Text>
              </Popover.Section>
              <ActionList
                actionRole="menuitem"
                items={[
                  {
                    content: 'Publish to Website',
                    icon: DomainLandingPageIcon,
                    onAction: handlePublishToWebsite,
                  },
                  {
                    content: 'Feature on Shop',
                    icon: CollectionFilledIcon,
                    onAction: handleFeatureOnShop,
                  },
                  {
                    content: 'Publish to Email Flows',
                    icon: EmailIcon,
                    onAction: handlePublishToEmailFlows,
                  },
                ]}
              />
            </Popover.Pane>
          </Popover>

          <Box>
            <Button
              onClick={() => setIsDownloadModalOpen(true)}
              icon={ImportIcon}
              size={buttonSize}
              disabled={disabled}
            />
          </Box>
        </InlineStack>

        {!!media && <MediaAssetCardQuickViewActions media={media} disabled={disabled} buttonSize={buttonSize} />}
      </InlineStack>

      {!!media && <DownloadMediaModal isOpen={isDownloadModalOpen} setIsOpen={setIsDownloadModalOpen} media={media} />}
    </>
  );
};

export default MediaAssetCardActions;
